import { useMutation } from '@apollo/client'
import {
  Stack,
  Typography,
  Button,
  CircularProgress,
  Box,
  keyframes,
  ChipProps,
  styled,
  Chip,
  Switch,
  LinearProgress,
} from '@mui/material'
import { useState, useCallback, useEffect, useMemo } from 'react'
import { MeetingRecorder, MeetingRecorderStatus } from 'src/__generated__/graphql'
import {
  GET_CALENDAR_EVENTS_QUERY,
  CREATE_ADHOC_MEETING_RECORDER,
  SEND_MEETING_RECORDER_USER_SIGNAL,
  updateCalendarEventRecordingStatusMutation,
} from 'src/graphql/queries'
import { useBoolean } from 'src/hooks/use-boolean'
import Iconify from '../iconify'
import { useAnalytics } from '../analytics'
import { isActiveMeetingRecorder } from './util'
import { useCalendarEvent } from '../user-calendar/hooks/use-calendar-event'

export function MeetingTransportControlsWrapper({ calendarEventId }: { calendarEventId: string }) {
  const { event, refetch } = useCalendarEvent(calendarEventId)
  const [activeRecorder, setActiveRecorder] = useState<MeetingRecorder | null | undefined>(undefined)
  useEffect(() => {
    setActiveRecorder(event?.meetingRecorders.find((mr) => isActiveMeetingRecorder(mr)) ?? null)
  }, [event])

  const handleAdhocMeetingRecorderCreated = useCallback(
    (meetingRecorder: MeetingRecorder) => {
      setActiveRecorder(meetingRecorder)
      refetch()
    },
    [refetch, setActiveRecorder]
  )

  if (activeRecorder === undefined || !event) {
    return <LinearProgress />
  }

  if (!event.meetingUrl) {
    return (
      <Typography variant="caption" color="text.secondary">
        This meeting is not a video conference
      </Typography>
    )
  }

  if (new Date(event.start) > new Date() && event.meetingUrl) {
    return <ScheduleRecorderControls recordingEnabled={event.recordMeeting ?? false} calendarEventId={event.id} />
  }

  if (activeRecorder == null && event?.meetingUrl) {
    return (
      <InviteToMeetingControls
        meetingUrl={event.meetingUrl}
        meetingId={event.meetingId}
        onAdhocMeetingRecorderCreated={handleAdhocMeetingRecorderCreated}
      />
    )
  }

  if (activeRecorder != null) {
    return <MeetingTransportControls status={activeRecorder.status} meetingRecorderId={activeRecorder.id} />
  }

  return (
    <Typography variant="caption" color="text.secondary">
      This meeting is not a video conference
    </Typography>
  )
}

function ScheduleRecorderControls({
  recordingEnabled,
  calendarEventId,
}: {
  recordingEnabled?: boolean
  calendarEventId?: string | null
}) {
  const { track } = useAnalytics()
  const updatingRecordingStatus = useBoolean(false)
  const [updateCalendarEventRecordingStatus] = useMutation(updateCalendarEventRecordingStatusMutation)

  const onRecordingSwitchChange = useCallback(
    async (click: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      click.stopPropagation()
      track('Click', {
        element: 'update_recording_status',
      })
      if (!calendarEventId) return
      updatingRecordingStatus.onTrue()
      try {
        await updateCalendarEventRecordingStatus({
          variables: {
            calendarEventId,
            recordMeeting: checked,
          },
          // ! TODO: Update the cache instead of refetching
          refetchQueries: [GET_CALENDAR_EVENTS_QUERY],
        })
      } finally {
        updatingRecordingStatus.onFalse()
      }
    },
    [track, updateCalendarEventRecordingStatus, updatingRecordingStatus, calendarEventId]
  )

  return (
    <Stack direction="row" spacing={1} width={1} justifyContent="start" alignItems="center">
      <Typography variant="caption" color="text.secondary" display={{ xs: 'none', sm: 'block' }}>
        Meeting recorder
      </Typography>
      <Stack direction="row" alignItems="center" justifyContent="center">
        <Switch
          sx={{
            // display: 'flex',
            ml: -0.5,
          }}
          color="primary"
          checked={recordingEnabled}
          disabled={updatingRecordingStatus.value}
          onChange={(click, checked) => {
            onRecordingSwitchChange(click, checked)
          }}
        />
        <Iconify
          inline
          icon="tabler:video-filled"
          width={20}
          flexShrink={0}
          // color={event?.event.extendedProps.recordMeeting ? theme.palette.primary.dark : theme.palette.text.disabled}
          sx={{ stroke: 'white', strokeWidth: 1, ml: -0.5 }}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {recordingEnabled ? 'Enabled' : 'Disabled'}
      </Typography>
    </Stack>
  )
}

export function MeetingTransportControls({
  status,
  meetingRecorderId,
}: {
  status: MeetingRecorderStatus
  meetingRecorderId: string
}) {
  const [internalStatus, setInternalStatus] = useState(status)
  const [loading, setLoading] = useState(false)
  const [sendMeetingRecorderUserSignal] = useMutation(SEND_MEETING_RECORDER_USER_SIGNAL)

  useEffect(() => {
    // if the status changes, we should reset the loading state
    setLoading(false)
    setInternalStatus(status)
  }, [status])

  const handleRecordingStop = useCallback(async () => {
    setLoading(true)
    setInternalStatus(MeetingRecorderStatus.InMeetingStopRequested)
    await sendMeetingRecorderUserSignal({
      variables: {
        meetingBotId: meetingRecorderId,
        event: {
          eventType: 'stop',
        },
      },
    })
  }, [sendMeetingRecorderUserSignal, meetingRecorderId])

  const handleRecordingPause = useCallback(async () => {
    setLoading(true)
    setInternalStatus(MeetingRecorderStatus.InMeetingPauseRequested)
    await sendMeetingRecorderUserSignal({
      variables: {
        meetingBotId: meetingRecorderId,
        event: {
          eventType: 'pause',
        },
      },
    })
  }, [sendMeetingRecorderUserSignal, meetingRecorderId])

  const handleRecordingResume = useCallback(async () => {
    setLoading(true)
    setInternalStatus(MeetingRecorderStatus.InMeetingResumeRequested)
    await sendMeetingRecorderUserSignal({
      variables: {
        meetingBotId: meetingRecorderId,
        event: {
          eventType: 'record',
        },
      },
    })
  }, [sendMeetingRecorderUserSignal, meetingRecorderId])

  const buttons = useMemo(() => {
    if (internalStatus === MeetingRecorderStatus.InvitedToMeeting) {
      return (
        <Stack direction="row" spacing={1}>
          <Button disabled variant="outlined" color="info" size="small" sx={{ minWidth: 'fit-content' }}>
            <CircularProgress color="inherit" size={16} />
          </Button>
        </Stack>
      )
    }

    if (internalStatus === MeetingRecorderStatus.JoiningCall) {
      return (
        <Stack direction="row" spacing={1}>
          <Button disabled variant="outlined" color="info" size="small" sx={{ minWidth: 'fit-content' }}>
            <CircularProgress color="inherit" size={16} />
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            onClick={() => handleRecordingStop()}
            startIcon={<Iconify icon="tabler:x" width={16} height={16} />}
          >
            Cancel
          </Button>
        </Stack>
      )
    }

    if (
      internalStatus === MeetingRecorderStatus.InMeetingNotRecording ||
      internalStatus === MeetingRecorderStatus.InWaitingRoom
    ) {
      return (
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            onClick={() => handleRecordingStop()}
            startIcon={<Iconify icon="tabler:x" width={16} height={16} />}
          >
            Cancel
          </Button>
        </Stack>
      )
    }
    if (internalStatus === MeetingRecorderStatus.InMeetingRecording) {
      return (
        <Stack direction="row" spacing={1}>
          <Button
            disabled={loading}
            variant="soft"
            color="warning"
            size="small"
            onClick={() => handleRecordingPause()}
            startIcon={<Iconify icon="tabler:pause" width={16} height={16} />}
          >
            Pause
          </Button>
          <Button
            disabled={loading}
            variant="soft"
            color="inherit"
            size="small"
            onClick={() => handleRecordingStop()}
            startIcon={<Iconify icon="tabler:square" width={16} height={16} />}
          >
            Stop
          </Button>
        </Stack>
      )
    }

    if (internalStatus === MeetingRecorderStatus.InMeetingPauseRequested) {
      return (
        <Button
          disabled
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<CircularProgress color="inherit" size={16} />}
        >
          Pausing...
        </Button>
      )
    }

    if (internalStatus === MeetingRecorderStatus.InMeetingStopRequested) {
      return (
        <Button
          disabled
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<CircularProgress color="inherit" size={16} />}
        >
          Stopping...
        </Button>
      )
    }

    if (internalStatus === MeetingRecorderStatus.InMeetingPaused) {
      return (
        <Stack direction="row" spacing={1}>
          <Button
            disabled={loading}
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleRecordingResume()}
            startIcon={<Iconify icon="tabler:play" width={16} height={16} />}
          >
            Resume
          </Button>
          <Button
            disabled={loading}
            variant="soft"
            color="inherit"
            size="small"
            onClick={() => handleRecordingStop()}
            startIcon={<Iconify icon="tabler:square" width={16} height={16} />}
          >
            Stop
          </Button>
        </Stack>
      )
    }

    if (internalStatus === MeetingRecorderStatus.InMeetingResumeRequested) {
      return (
        <Button disabled variant="contained" color="primary" size="small" startIcon={<CircularProgress size={16} />}>
          Resuming...
        </Button>
      )
    }
    return null
  }, [internalStatus, handleRecordingPause, handleRecordingResume, handleRecordingStop, loading])

  return (
    <Stack direction="row" spacing={1} width={1} justifyContent="center" alignItems="center">
      <Typography variant="caption" color="text.secondary" display={{ xs: 'none', sm: 'block' }}>
        Meeting recorder
      </Typography>
      <MeetingRecordingStatusChip status={status} />
      <Box mr="auto" />
      {buttons}
    </Stack>
  )
}

function InviteToMeetingControls({
  meetingUrl,
  meetingId,
  onAdhocMeetingRecorderCreated,
}: {
  meetingUrl?: string | null
  meetingId?: string | null
  onAdhocMeetingRecorderCreated: (meetingRecorder: MeetingRecorder) => void
}) {
  const [loading, setLoading] = useState(false)
  const [createAdhocMeetingRecorder] = useMutation(CREATE_ADHOC_MEETING_RECORDER)
  const handleStartRecording = useCallback(async () => {
    if (!meetingUrl) {
      return
    }
    setLoading(true)
    try {
      const result = await createAdhocMeetingRecorder({
        variables: {
          meetingUrl,
          meetingId,
        },
      })
      if (result.data?.createAdHocMeetingRecorder) {
        onAdhocMeetingRecorderCreated(result.data.createAdHocMeetingRecorder)
      }
      // Add a timer to wait for the meeting recorder settings to be updated
      setTimeout(() => {
        setLoading(false)
      }, 10000)
    } catch (error) {
      console.error(error)
      setLoading(false)
    }
  }, [createAdhocMeetingRecorder, meetingUrl, meetingId, onAdhocMeetingRecorderCreated])

  if (!meetingUrl) {
    return (
      <Stack>
        <Typography variant="caption" color="text.secondary">
          This meeting does not have a recording URL
        </Typography>
      </Stack>
    )
  }

  return (
    <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
      <Typography variant="caption" color="text.secondary" display={{ xs: 'none', sm: 'block' }}>
        Meeting recorder
      </Typography>
      <Button
        variant="contained"
        color="primary"
        size="small"
        sx={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        onClick={() => handleStartRecording()}
        disabled={loading}
        startIcon={
          loading ? (
            <CircularProgress color="inherit" size={16} />
          ) : (
            <Iconify icon="tabler:video-plus" width={16} height={16} />
          )
        }
      >
        Record Meeting
      </Button>
    </Stack>
  )
}

const pulseKeyframes = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0.4;
  }
`

function MeetingRecordingStatusChip({ status }: { status: MeetingRecorderStatus }) {
  const statusChip = useMemo(() => {
    if (status === MeetingRecorderStatus.JoiningCall) {
      return <StyledMeetingRecordingStatusChip label="Joining call" color="info" />
    }
    if (status === MeetingRecorderStatus.InWaitingRoom) {
      return <StyledMeetingRecordingStatusChip label="In waiting room" color="info" />
    }
    if (status === MeetingRecorderStatus.InMeetingRecording) {
      return (
        <StyledMeetingRecordingStatusChip
          label="Recording"
          color="error"
          icon={
            <Iconify
              sx={{ animation: `${pulseKeyframes} 800ms infinite alternate ease-in-out` }}
              inline
              icon="tabler:circle-filled"
              width={12}
              height={12}
            />
          }
        />
      )
    }
    if (status === MeetingRecorderStatus.InMeetingPauseRequested) {
      return <StyledMeetingRecordingStatusChip label="Pausing" color="warning" />
    }
    if (status === MeetingRecorderStatus.InMeetingResumeRequested) {
      return <StyledMeetingRecordingStatusChip label="Resuming" color="warning" />
    }
    if (status === MeetingRecorderStatus.InMeetingStopRequested) {
      return <StyledMeetingRecordingStatusChip label="Stopping" color="error" />
    }
    if (status === MeetingRecorderStatus.InMeetingPaused) {
      return <StyledMeetingRecordingStatusChip label="Paused" color="default" />
    }
    if (status === MeetingRecorderStatus.InMeetingNotRecording) {
      return <StyledMeetingRecordingStatusChip label="Not recording" color="warning" />
    }
    if (status === MeetingRecorderStatus.RecordingComplete) {
      return <StyledMeetingRecordingStatusChip label="Recording complete" color="success" />
    }
    if (status === MeetingRecorderStatus.Scheduled) {
      return <StyledMeetingRecordingStatusChip label="On its way" color="info" />
    }
    if (status === MeetingRecorderStatus.InvitedToMeeting) {
      return <StyledMeetingRecordingStatusChip label="On its way" color="info" />
    }

    return <StyledMeetingRecordingStatusChip label={status} color="secondary" />
  }, [status])
  return statusChip
}

function StyledMeetingRecordingStatusChip({
  label,
  color,
  icon,
}: {
  label: string
  color: ChipProps['color']
  icon?: React.ReactElement | undefined
}) {
  return <StyledRecordingChip label={label} color={color} size="small" variant="soft" icon={icon} />
}

const StyledRecordingChip = styled(Chip)(({ theme }) => ({
  border: '1px solid',
  height: 22,
  borderRadius: 10,
  paddingLeft: theme.spacing(0.5),
  paddingRight: theme.spacing(0.5),
  fontWeight: theme.typography.fontWeightBold,
}))
